import cx from "classnames"
import { Link } from "gatsby"
import React, { useLayoutEffect, useState } from "react"
import { routes } from "../helpers"
import { useIntl, useWindowSize } from "../hooks"
import { IconArrow, logo } from "../images"

export default function Navbar() {
  const { t } = useIntl()

  const [isNavOpen, setIsNavOpen] = useState(false)

  const window = useWindowSize()

  const isMediaWidthLaptop = window.width && window.width > 1024

  if (isMediaWidthLaptop && isNavOpen) setIsNavOpen(false)

  useLayoutEffect(() => {
    if (isNavOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = "visible"
    }
  }, [isNavOpen])

  return (
    <div className={cx("laptop:py-1.5 w-full bg-white z-40 relative")}>
      <div className="container flex items-center flex-wrap laptop:flex-no-wrap font-semibold">
        <div
          className={cx(
            "w-full laptop:w-auto py-2.5 laptop:mr-6 z-50 flex items-center justify-between bg-white flex-shrink-0",
          )}
        >
          <div className="flex items-center">
            <Link to={routes.index} className="cursor-pointer">
              <img src={logo} width="264" height="36" alt="socialie logo" className="w-33" />
            </Link>
          </div>
          <div className="ml-auto laptop:hidden flex items-center">
            <MobileMenu isNavOpen={isNavOpen} setIsNavOpen={setIsNavOpen} />
          </div>
        </div>
        {isMediaWidthLaptop && (
          <div className={cx("w-full mt-0 flex flex-row px-0 pb-0 pt-0 relative justify-end")}>
            <div className="w-full flex font-sans text-16 flex-row items-center space-y-0 space-x-6 justify-end">
              <DesktopNavLink route={routes.howItWorks} text={t("Navbar__NavItem--HowItWorks")} />
              <DesktopNavLink route={routes.solutions} text={t("Navbar__NavItem--Solutions")} />
              <DesktopNavLink route={routes.about} text={t("Navbar__NavItem--About")} />
              <SignInRoute />
              <Link
                to={routes.requestDemo}
                className="w-full phablet:w-24 tablet:w-24 laptop:w-fit btn btn-sm btn-primary text-center whitespace-no-wrap"
              >
                {t("Navbar__Button--RequestDemo")}
              </Link>
            </div>
          </div>
        )}
      </div>
      <div
        className={cx("transform", {
          "hidden -translate-y-100": !isNavOpen,
          "translate-y-0": isNavOpen,
        })}
      >
        <MobileNav />
      </div>
    </div>
  )
}

interface MobileNavProps {
  isNavOpen: boolean
}

function MobileNav() {
  const { t } = useIntl()
  return (
    <div className="h-screen container">
      <div className="mt-2 mb-6 space-y-3">
        <MobileNavLink route={routes.howItWorks} text={t("Navbar__NavItem--HowItWorks")} />
        <MobileNavLink route={routes.solutions} text={t("Navbar__NavItem--Solutions")} />
        <MobileNavLink route={routes.about} text={t("Navbar__NavItem--About")} />
        <div className="mt-6 laptop:mt-0 ml-auto w-full flex flex-col-reverse laptop:flex-row laptop:justify-end items-center phablet:items-start tablet:items-start laptop:items-center laptop:space-x-4">
          <SignInRoute />
          <Link
            to={routes.requestDemo}
            className="w-full phablet:w-24 tablet:w-24 laptop:w-fit btn btn-sm btn-primary text-center"
          >
            {t("Navbar__Button--RequestDemo")}
          </Link>
        </div>
      </div>
    </div>
  )
}

function MobileNavLink(props: Props) {
  const { text, route } = props
  return (
    <div className="group overflow-hidden flex flex-col justify-between">
      <Link to={route} className="mt-1 flex-1 font-serif text-h4 leading-h4 text-body cursor-pointer">
        {text}
      </Link>
      <div className="-ml-1px h-0.5 mt-0.5 w-fit bg-socialiePink transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300 ease-in-out" />
    </div>
  )
}

interface Props {
  text: string
  route: string
}

function DesktopNavLink(props: Props) {
  const { text, route } = props
  return (
    <div className="group overflow-hidden flex flex-col justify-between">
      <Link to={route} className="mt-1 flex-1 text-16 text-body leading-p cursor-pointer">
        {text}
      </Link>
      <div className="-ml-1px h-0.5 mt-0.5 w-fit laptop:w-full bg-socialiePink transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300 ease-in-out" />
    </div>
  )
}

function SignInRoute() {
  const { t } = useIntl()
  return (
    <div className="w-full tablet:w-fit laptop:w-fit group overflow-hidden flex flex-col justify-between">
      <a
        href={routes.signIn}
        rel="noopener noreferrer"
        target="_blank"
        className="mt-2.5 laptop:mt-1 w-full phablet:w-24 tablet:w-24 btn btn-sm btn-outline justify-center laptop:hidden group text-body leading-p cursor-pointer"
      >
        <span className="flex items-center justify-center">
          <p className="leading-p"> {t("Navbar__NavItem--SignIn")}</p>
          <IconArrow className="inline-block laptop:hidden ml-1 w-2 h-2 pt-2px transition-transform duration-100 ease-in-out transform group-hover:translate-x-0.5" />
        </span>
      </a>
      <a className="hidden laptop:flex laptop:mt-0.5" href={routes.signIn} rel="noopener noreferrer" target="_blank">
        <span className="flex items-center">
          <p className="text-16 text-body leading-p"> {t("Navbar__NavItem--SignIn")}</p>
        </span>
      </a>
      <div className="-ml-1px h-0.5 mt-0.5 w-full hidden laptop:block bg-socialiePink transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300 ease-in-out" />
    </div>
  )
}

interface MobileMenuProps {
  isNavOpen: boolean
  setIsNavOpen: (isNavOpen: boolean) => void
}

function MobileMenu(props: MobileMenuProps) {
  const { isNavOpen, setIsNavOpen } = props
  const { t } = useIntl()
  return (
    <div className="flex items-center">
      <div
        className={cx("se:hidden phablet:hidden mr-4 transition-opacity duration-300 ease-in-out", {
          "opacity-0": isNavOpen,
          "opacity-100": !isNavOpen,
        })}
      >
        <a href={routes.signIn} rel="noopener noreferrer" target="_blank" className="text-16 text-body leading-h6">
          {t("Navbar__NavItem--SignIn")}
        </a>
      </div>
      <button
        className="-mt-0.5 focus:outline-none w-4 h-2 relative cursor-pointer"
        onClick={() => setIsNavOpen(!isNavOpen)}
      >
        {/* Hamburger menu */}
        {[1, 2, 3, 4].map((el) => (
          <span
            key={el}
            className={cx(
              "block absolute w-3.5 h-2px bg-bodyText rounded-md left-0 transform transition-all duration-300 ease-in-out",
              {
                "top-0 opacity-100 rotate-0 transition-all duration-300 ease-in-out": el === 1 && !isNavOpen,
                "top-1 rotate-0 transition-all duration-300 ease-in-out": (el === 2 || el === 3) && !isNavOpen,
                "top-2 opacity-100 rotate-0 transition-all duration-300 ease-in-out": el === 4 && !isNavOpen,
                "top-1 transform opacity-0 transition-all duration-100 ease-in-out":
                  (el === 1 || el === 4) && isNavOpen,
                "rotate-45 transition-all duration-300 ease-in-out": el === 2 && isNavOpen,
                "-rotate-45 transition-all duration-300 ease-in-out": el === 3 && isNavOpen,
              },
            )}
          />
        ))}
      </button>
    </div>
  )
}
